import React, { useState, useEffect, useMemo } from 'react';
import { TextField, Select, CircularProgress, Box, Typography } from '@mui/material';
import ModalWrapper from './modalWrapper/basicModalWrapper';
import { fetchEnvironmentList } from '../../utils/api/environment_apis';
import { getTestGroupSummary } from '../../utils/api/testgroup_apis';
import { createTestLaunch } from '../../utils/api/testlaunch_apis';
import { STATUS } from '../../utils/api/sharedfunction';
import { useSnackbar } from '../../utils/useSnackbar';

function ExecuteTestGroupModal({ open, onClose, onSuccess, group, projectId }) {
  const [environments, setEnvironments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingEnvironments, setFetchingEnvironments] = useState(false);
  const [testScope, setTestScope] = useState({ total: 0, automated: 0, manual: 0 });
  const [fetchingTestScope, setFetchingTestScope] = useState(false);
  const { showSnackbar } = useSnackbar();

  // Fetch environments and test scope when modal opens
  useEffect(() => {
    if (open && projectId && group?.id) {
      fetchEnvironments();
      fetchTestScope();
    } else if (open && projectId) {
      // Only fetch environments if group is not available
      fetchEnvironments();
    }
  }, [open, projectId, group?.id]);

  const fetchEnvironments = async () => {
    setFetchingEnvironments(true);
    try {
      const response = await fetchEnvironmentList();
      if (response.status === STATUS.SUCCESS) {
        setEnvironments(response.data || []);
      } else {
        showSnackbar(response.error || 'Failed to fetch environments', 'error');
      }
    } catch (error) {
      console.error('Error fetching environments:', error);
      showSnackbar('Error fetching environments', 'error');
    } finally {
      setFetchingEnvironments(false);
    }
  };

  const fetchTestScope = async () => {
    if (!group?.id) return;
    
    setFetchingTestScope(true);
    try {
      const response = await getTestGroupSummary({ testGroupId: group.id });
      if (response.status === STATUS.SUCCESS) {
        // API returns data directly
        setTestScope(response.data || { total: 0, automated: 0, manual: 0 });
      } else {
        setTestScope({ total: 0, automated: 0, manual: 0 });
      }
    } catch (error) {
      console.error('Error fetching test scope:', error);
      showSnackbar('Error fetching test scope', 'error');
      // Set default values on error
      setTestScope({ total: 0, automated: 0, manual: 0 });
    } finally {
      setFetchingTestScope(false);
    }
  };

  // Field definitions for ModalWrapper
  const fields = useMemo(() => [
    {
      Component: TextField,
      name: 'launchName',
      label: 'Launch Name',
      autoFocus: true,
      placeholder: group ? `${group.name}-${new Date().toISOString().slice(0, 10)}-${Math.floor(Math.random()*10000)}` : '',
      fullWidth: true,
      // disabled: !!group,
    },
    {
      Component: TextField,
      name: 'testScope',
      label: 'Test Scope',
      value: !group?.id ? 'N/A' : fetchingTestScope ? 'Loading...' : testScope.total === 0 ? 'No test cases found - Cannot execute' : `${testScope.automated || 0} automated : ${testScope.manual || 0} manual - ${testScope.total || 0} in total`,
      fullWidth: true,
      disabled: true,
      error: testScope.total === 0,
      helperText: testScope.total === 0 ? 'Please add test cases to the group before executing' : '',
    },
    {
      Component: Select,
      name: 'environment',
      inputLabel: 'Environment',
      // required: false,
      fullWidth: true,
      options: environments.map(env => ({
        value: env.id,
        label: env.name,
      })),
      disabled: fetchingEnvironments,
      helperText: fetchingEnvironments ? 'Loading environments...' : '',
    },
  ], [group, environments, fetchingEnvironments, testScope, fetchingTestScope]);

  // Buttons for ModalWrapper
  const buttons = [
    {
      label: 'Cancel',
      onClick: onClose,
      variant: 'outline',
      className: 'primary',
      disabled: loading,
    },
    {
      label: loading ? 'Executing...' : 'Execute Now',
      isdefault: true,
      variant: 'primary',
      className: 'white bg-blue600',
      disabled: loading || fetchingEnvironments || fetchingTestScope || testScope.total === 0,
    },
  ];

  // Handle submit
  const handleSubmit = async (data) => {
    // Check if test group is empty before proceeding
    if (testScope.total === 0) {
      showSnackbar('Cannot execute an empty test group. Please add test cases to the group first.', 'error');
      return;
    }

    const config = {
      ...(data.environment && { environmentId: data.environment }),
    };

    setLoading(true);
    try {
      const response = await createTestLaunch({
        testgroupId: group.id,
        name: data.launchName,
        isSequential: true,
        config: config
      });
      
      if (response.status === STATUS.SUCCESS) {
        showSnackbar(response.message || `Test group execution started: ${group.name}`, 'success');
        onSuccess && onSuccess();
        onClose();
      } else {
        showSnackbar(response.error || `Failed to execute group: ${group.name}`, 'error');
      }
    } catch (error) {
      console.error('Error executing test group:', error);
      showSnackbar(error.message || `Failed to execute group: ${group.name}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalWrapper
      open={open}
      onClose={onClose}
      title="Execute Test Group"
      fields={fields}
      buttons={buttons}
      onSubmit={handleSubmit}
      loading={loading}
      initialValues={{}}
    />
  );
}

export default ExecuteTestGroupModal; 